import { useState, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Package,
  Menu as MenuIcon,
  Users,
  Archive,
  BarChart,
  Settings,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import { useOnClickOutside } from "../../hooks/useOnClickOutside";

// Icons
const icons = {
  home: <Home className="w-5 h-5" />,
  orders: <Package className="w-5 h-5" />,
  menu: <MenuIcon className="w-5 h-5" />,
  customers: <Users className="w-5 h-5" />,
  inventory: <Archive className="w-5 h-5" />,
  reports: <BarChart className="w-5 h-5" />,
  settings: <Settings className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  chevronDown: <ChevronDown className="w-4 h-4" />,
  chevronRight: <ChevronRight className="w-4 h-4" />,
};

// Menu structure for navigation
const menuItems = [
  { path: "/dashboard", icon: icons.home, label: "Dashboard" },
  { path: "/orders", icon: icons.orders, label: "Orders" },
  { path: "/menu", icon: icons.menu, label: "Menu" },
  { path: "/customers", icon: icons.customers, label: "Customers" },
  { path: "/inventory", icon: icons.inventory, label: "Inventory" },
  { path: "/reports", icon: icons.reports, label: "Reports" },
  {
    path: "/settings",
    icon: icons.settings,
    label: "Settings",
    submenu: [
      { path: "/settings/restaurant", label: "Restaurant Info" },
      { path: "/settings/payment", label: "Payment Methods" },
      { path: "/settings/taxes", label: "Taxes" },
      { path: "/settings/order-status", label: "Order Statuses" },
    ],
  },
  { path: "/users", icon: icons.users, label: "Users" },
];

interface SidebarProps {
  isMobile: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar = ({ isMobile, isOpen, onClose }: SidebarProps) => {
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close sidebar when clicking outside on mobile
  useOnClickOutside(sidebarRef, () => {
    if (isMobile && isOpen) {
      onClose();
    }
  });

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Toggle submenu open/closed
  const toggleSubmenu = (path: string, e: React.MouseEvent) => {
    e.preventDefault();
    setOpenSubmenu(openSubmenu === path ? null : path);
  };

  // Handle click on a navigation item
  const handleNavClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  // Base classes for the sidebar - FIXED for all cases
  const sidebarClasses = `
    bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800
    h-screen flex flex-col transition-all duration-300 ease-in-out
    fixed top-0 left-0 z-40 w-64
    ${isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"}
    ${isMobile ? "shadow-lg" : ""}
    overflow-y-auto
  `;

  // Overlay for mobile
  const overlayClasses = `
    fixed inset-0 bg-black bg-opacity-50 z-30 transition-opacity duration-300
    ${isMobile && isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
  `;

  return (
    <>
      {/* Mobile overlay */}
      <div className={overlayClasses} onClick={onClose} />

      {/* Sidebar */}
      <div ref={sidebarRef} className={sidebarClasses}>
        {/* Logo area */}
        <div className="p-4 flex items-center justify-center border-b border-gray-200 dark:border-gray-800">
          <img
            alt="Enzo's Restaurant"
            className="h-16 rounded-full border-2 border-red-600"
            src="/logo.svg"
          />
          <span className="ml-2 text-xl font-semibold">Enzo's</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                {item.submenu ? (
                  <div>
                    <button
                      className={`w-full justify-start text-left px-4 py-2 mb-1 ${
                        isActive(item.path)
                          ? "bg-primary-100 dark:bg-primary-300 text-primary-600 dark:text-primary-900"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                      onClick={(e) => toggleSubmenu(item.path, e)}
                    >
                      <span className="flex items-center">
                        <span className="mr-3">{item.icon}</span>
                        <span>{item.label}</span>
                        <span className="ml-auto">
                          {openSubmenu === item.path
                            ? icons.chevronDown
                            : icons.chevronRight}
                        </span>
                      </span>
                    </button>
                    {openSubmenu === item.path && (
                      <ul className="pl-4">
                        {item.submenu.map((subItem) => (
                          <li key={subItem.path}>
                            <NavLink
                              className={({ isActive }) => `
                                flex items-center px-4 py-2 rounded-md
                                ${
                                  isActive
                                    ? "bg-primary-100 dark:bg-primary-300 text-primary-600 dark:text-primary-900"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800"
                                }
                              `}
                              to={subItem.path}
                              onClick={handleNavClick}
                            >
                              <span>{subItem.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <NavLink
                    className={({ isActive }) => `
                      flex items-center px-4 py-2 rounded-md
                      ${
                        isActive
                          ? "bg-primary-100 dark:bg-primary-300 text-primary-600 dark:text-primary-900"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      }
                    `}
                    to={item.path}
                    onClick={handleNavClick}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
